import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { apiGet, apiPost } from '../../utils/api';
import { normalizeReviewHtmlImages } from '../../utils/reviewImage';
import { sanitizeReviewHtml } from '../../utils/reviewHtml';
import { createReviewUploadPlugin } from '../../utils/reviewUploadAdapter';
import { usePageTitle } from '../../contexts/PageTitleContext';
import '../../css/Review/review.css';

const hasMeaningfulHtml = (value) => {
  const container = document.createElement('div');
  container.innerHTML = sanitizeReviewHtml(value);
  return !!container.querySelector('img[src]') || !!container.textContent.replace(/[\s\u00a0\u200b\ufeff]/g, '');
};

export default function ReviewWrite() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { setPageTitle } = usePageTitle();
  const isEdit = id !== undefined;
  const reviewId = /^\d+$/.test(id || '') ? Number(id) : 0;
  const reservationRaw = searchParams.get('reservation_idx') || '';
  const reservationIdx = /^\d+$/.test(reservationRaw) ? Number(reservationRaw) : 0;
  const page = Number(searchParams.get('page'));
  const pageSuffix = Number.isInteger(page) && page > 1 ? `?page=${page}` : '';
  const routeKey = isEdit ? `edit:${id}` : `write:${reservationIdx}`;
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [meta, setMeta] = useState(null);
  const [loadedKey, setLoadedKey] = useState('');
  const [title, setTitle] = useState('');
  const [contentHtml, setContentHtml] = useState('');
  const [initialContent, setInitialContent] = useState('');
  const [editorError, setEditorError] = useState('');
  const [isEditorReady, setIsEditorReady] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const editorRef = useRef(null);
  const saveRef = useRef(null);
  const activeRoute = useRef(routeKey);
  const generationRef = useRef(0);
  activeRoute.current = routeKey;

  useEffect(() => { setPageTitle(isEdit ? '리뷰 수정' : '리뷰 작성'); }, [isEdit, setPageTitle]);

  useEffect(() => {
    let mounted = true;
    const generation = ++generationRef.current;
    setMeta(null);
    setLoadedKey('');
    setTitle('');
    setContentHtml('');
    setInitialContent('');
    setError('');
    setSaveError('');
    setEditorError('');
    setIsEditorReady(false);
    setIsSaving(false);
    editorRef.current = null;
    saveRef.current = null;
    const target = isEdit ? reviewId : reservationIdx;
    if (!Number.isSafeInteger(target) || target <= 0) {
      setError(isEdit ? '유효한 리뷰 번호가 필요합니다.' : '유효한 예약 번호가 필요합니다.');
      setIsLoading(false);
      return () => { mounted = false; generationRef.current = generation + 1; };
    }
    setIsLoading(true);
    apiGet(isEdit ? '/review/get_review_edit_meta.php' : '/review/get_review_write_meta.php',
      isEdit ? { id: reviewId } : { reservation_idx: reservationIdx })
      .then((res) => {
        if (!mounted) return;
        if (!res?.success) { setError(res?.message || '리뷰 정보를 불러오지 못했습니다.'); return; }
        setMeta(res);
        setLoadedKey(routeKey);
        if (isEdit) {
          const html = sanitizeReviewHtml(normalizeReviewHtmlImages(res.review?.content_html || ''));
          setTitle(res.review?.title || '');
          setContentHtml(html);
          setInitialContent(html);
        }
      })
      .catch((err) => { if (mounted) setError(err?.response?.data?.message || '리뷰 정보를 불러오지 못했습니다.'); })
      .finally(() => { if (mounted) setIsLoading(false); });
    return () => { mounted = false; generationRef.current = generation + 1; };
  }, [isEdit, reviewId, reservationIdx, routeKey]);

  const reviewWritable = meta?.review_writable === true;
  const existingReviewId = Number(meta?.review_existing_id || 0);
  const canSubmit = !!meta && loadedKey === routeKey && !error && !isLoading && (
    isEdit ? meta.review_editable === true && meta.review?.can_edit === true
      : existingReviewId <= 0 && reviewWritable
  );
  const csrfToken = meta?.csrf_token || '';
  // A→B→A navigation can leave the first A's asynchronous editor callbacks pending.
  // Capture this mount's generation, not just the route, before creating callbacks.
  const editorGeneration = generationRef.current;
  const isCurrentEditor = () => activeRoute.current === routeKey && generationRef.current === editorGeneration;
  const editorConfig = useMemo(() => ({
    toolbar: ['heading', '|', 'bold', 'italic', 'link', 'bulletedList', 'numberedList', '|',
      'outdent', 'indent', '|', 'imageUpload', 'blockQuote', 'insertTable', 'undo', 'redo'],
    image: { toolbar: ['imageTextAlternative', '|', 'imageStyle:inline', 'imageStyle:block', 'imageStyle:side'] },
    extraPlugins: [createReviewUploadPlugin(csrfToken)],
  }), [csrfToken]);
  const leaveEditor = () => {
    if (saveRef.current) return;
    navigate(isEdit ? `/review/${reviewId}${pageSuffix}` : '/review');
  };

  const onSubmit = async () => {
    if (!canSubmit || saveRef.current) return;
    if (!csrfToken) { setSaveError('인증 정보를 확인할 수 없습니다. 페이지를 새로고침한 후 다시 시도해주세요.'); return; }
    const safeTitle = title.trim();
    if (!safeTitle) { alert('제목을 입력해주세요.'); return; }
    if (!isEditorReady) { alert('에디터가 아직 준비되지 않았습니다. 잠시 후 다시 시도해주세요.'); return; }
    if (editorRef.current?.plugins?.get('PendingActions')?.hasAny) {
      alert('이미지 업로드가 완료된 후 저장해주세요.');
      return;
    }
    const safeHtml = sanitizeReviewHtml(editorRef.current ? editorRef.current.getData() : contentHtml);
    if (!hasMeaningfulHtml(safeHtml)) { alert('내용을 입력해주세요.'); return; }
    const ticket = { key: routeKey, generation: generationRef.current };
    saveRef.current = ticket;
    const isCurrent = () => activeRoute.current === ticket.key && generationRef.current === ticket.generation && saveRef.current === ticket;
    setIsSaving(true);
    setSaveError('');
    try {
      const res = await apiPost(isEdit ? '/review/update_review.php' : '/review/create_review.php', {
        ...(isEdit ? { id: reviewId, revision_token: meta.review.revision_token } : { reservation_idx: reservationIdx }),
        title: safeTitle,
        content_html: safeHtml,
      }, { headers: { 'X-CSRF-Token': csrfToken } });
      if (!isCurrent()) return;
      if (res?.success) navigate(`/review/${isEdit ? reviewId : res.id}${pageSuffix}`, { replace: true });
      else if (!isEdit && res?.existing_review_id) {
        alert(res.message || '이미 작성된 리뷰가 있습니다.');
        navigate(`/review/${res.existing_review_id}`);
      } else setSaveError(res?.message || '리뷰 저장에 실패했습니다.');
    } catch (err) {
      if (!isCurrent()) return;
      const existingId = Number(err?.response?.data?.existing_review_id || 0);
      if (!isEdit && Number.isSafeInteger(existingId) && existingId > 0) {
        alert(err.response.data.message || '이미 작성된 리뷰가 있습니다.');
        navigate(`/review/${existingId}`);
        return;
      }
      setSaveError(err?.response?.status === 409
        ? '리뷰가 다른 곳에서 변경되었습니다. 작성 중인 내용을 복사해 둔 뒤 페이지를 새로고침해주세요.'
        : err?.response?.status === 403
          ? '요청 권한 또는 인증 정보가 변경되었습니다. 작성 중인 내용을 복사해 둔 뒤 페이지를 새로고침해주세요.'
        : err?.response?.data?.message || '리뷰 저장에 실패했습니다. 입력 내용은 유지됩니다.');
    } finally {
      if (isCurrent()) { saveRef.current = null; setIsSaving(false); }
    }
  };

  return (
    <div className="review-page"><div className="review-container">
      <button type="button" className="review-line-btn" disabled={isSaving} onClick={leaveEditor}>이전으로</button>
      <div className="review-write-box">
        <h2>{isEdit ? '리뷰 수정' : '리뷰 작성'}</h2>
        {isLoading && <div className="review-status">정보 확인 중...</div>}
        {!isLoading && error && <div className="review-status error" role="alert">{error}</div>}
        {!isLoading && !error && meta && loadedKey === routeKey && <>
          <div className="review-write-meta">
            <p>예약번호: {meta.reservation_idx || reservationIdx}</p>
            <p>상품명: {meta.ship_name || '-'}</p>
            <p>출항일: {meta.sail_date || '-'}</p>
            <p>도착일: {meta.end_date || '-'}</p>
            {!isEdit && <p>리뷰 가능일: {meta.review_available_at || '-'}</p>}
          </div>
          {isEdit && meta.review?.is_visible === false && <div className="review-status">비공개 처리된 리뷰입니다. 수정하더라도 공개 상태로 변경되지 않습니다.</div>}
          {!isEdit && existingReviewId > 0 && <div className="review-status">
            이미 작성된 리뷰가 있습니다.
            <button type="button" className="review-line-btn inline" onClick={() => navigate(`/review/${existingReviewId}`)}>작성한 리뷰 보기</button>
          </div>}
          {!canSubmit && (isEdit || existingReviewId <= 0) && <div className="review-status">
            {meta.review_reason === 'review_deleted'
              ? '삭제한 리뷰의 복구·재작성은 고객센터에 문의해주세요.'
              : `현재는 리뷰 ${isEdit ? '수정' : '작성'}이 불가능합니다.`}
          </div>}
          {canSubmit && <div className="review-form">
            <input type="text" aria-label="리뷰 제목" value={title} disabled={isSaving} onChange={(e) => setTitle(e.target.value)} placeholder="제목을 입력해주세요." maxLength={200} />
            {editorError && <div className="review-status error" role="alert">{editorError}</div>}
            <div className="review-editor-wrap review-ckeditor-wrap">
              <CKEditor key={`${routeKey}:${editorGeneration}`} editor={ClassicEditor} data={initialContent} config={editorConfig} disabled={isSaving}
                onReady={(editor) => { if (isCurrentEditor()) { editorRef.current = editor; setIsEditorReady(true); setEditorError(''); } }}
                onChange={(_event, editor) => { if (isCurrentEditor()) setContentHtml(editor.getData() || ''); }}
                onError={(_error, details = {}) => {
                  if (!isCurrentEditor()) return;
                  editorRef.current = null;
                  setIsEditorReady(false);
                  if (!details.willEditorRestart) setEditorError('에디터를 불러오지 못했습니다.');
                }}
              />
            </div>
            {saveError && <div className="review-status error" role="alert">{saveError}</div>}
            <div className="review-detail-actions">
              <button type="button" className="review-line-btn" disabled={isSaving} onClick={leaveEditor}>취소</button>
              <button type="button" className="review-submit-btn" onClick={onSubmit} disabled={isSaving || !isEditorReady}>{isSaving ? '저장 중...' : isEdit ? '수정 저장' : '저장'}</button>
            </div>
          </div>}
        </>}
      </div>
    </div></div>
  );
}
